import {
  ChangeDetectionStrategy, Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnInit
} from '@angular/core';
import intersectionBy from 'lodash-es/intersectionBy';

import { CheckboxFilter } from '@shared/filters/entities/fetch-filters.response';
import { UniqueIdPipe } from '@app/pipes/unique-id.pipe';
import { ChipFilterSize, ChipFilterState } from '@design-system/components/molecules/chip-filter/entities/chip-filter.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';

@Component({
  selector: 'app-stars-filter',
  templateUrl: './stars-filter.component.html',
  styleUrls: ['./stars-filter.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class StarsFilterComponent implements OnInit, OnChanges {

  @Input() label: string;
  @Input() list: CheckboxFilter[] = [];
  @Input() disableMultipleOptions: boolean;
  @Input() pickedStars: string[] = [];
  @Input() isClear = false;
  @Input() index = -1;
  @Input() individualFilter: boolean;

  @Output() selectStar: EventEmitter<CheckboxFilter[]> = new EventEmitter<CheckboxFilter[]>();
  @Output() clearSelectedItems: EventEmitter<void> = new EventEmitter<void>();

  public selectedItems: CheckboxFilter[] = [];

  private readonly filterSelector = 'filter-';
  private readonly starSelector = '-star';

  public readonly chipFilterSize = ChipFilterSize.SM;
  public readonly icons = Icon;

  constructor(
    private readonly uniqueIdPipe: UniqueIdPipe
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['isClear'] && changes?.['isClear']?.currentValue) {
      this.emptyStarsFilter();
    }
    if (changes?.['pickedStars']?.currentValue) {
      const picked = changes?.['pickedStars']?.currentValue;
      const checkboxes: CheckboxFilter[] = picked.map(item => ({ Text: item, Count: 0 }));
      this.selectedItems = intersectionBy(this.stars, checkboxes, 'Text');
    }
  }

  ngOnInit(): void {
    this.initComponent();
  }

  get stars(): CheckboxFilter[] {
    return this.list.filter(star => star.Text !== '0');
  }

  public initComponent(): void {
    const selectionByPickedStars: CheckboxFilter[] = this.pickedStars.map(item => ({ Text: item, Count: 0 }));
    const intersection = intersectionBy(this.stars, selectionByPickedStars, 'Text');
    this.selectedItems = intersection;
  }

  public itemIsSelected(item: CheckboxFilter): boolean {
    return this.pickedStars.map(_item => _item).includes(item.Text);
  }

  public choiceItem(star: CheckboxFilter): void {
    this.updateSelectedItems(star);
    this.selectStar.emit(this.selectedItems);
  }

  public emptyStarsFilter(): void {
    this.selectedItems = [];
    this.clearSelectedItems.emit();
  }

  public uniqueId(starId: number): string {
    return this.uniqueIdPipe.transform(this.filterSelector, this.index, `${this.starSelector}-${starId}`);
  }

  public trackById(index: number): number {
    return index;
  }

  public updateSelectedItems(item: CheckboxFilter): void {
    if (this.disableMultipleOptions) {
      this.selectedItems = this.selectedItems.map(_item => _item.Text).includes(item.Text) ? [] : [item];
    } else {
      if (this.selectedItems.map(_item => _item.Text).includes(item.Text)) {
        this.selectedItems = this.selectedItems.filter(_item => _item.Text !== item.Text);
      } else {
        this.selectedItems = this.selectedItems.concat(item);
      }
    }
  }

  public chipFilterState(isActive: boolean): ChipFilterState {
    return isActive ? ChipFilterState.Highlight : ChipFilterState.Default;
  }

}
